import { supabase } from "@/integrations/supabase/client";

interface PrivateAlbum {
  id: string;
  name: string;
  user_id: string;
  created_at: string;
  media_count?: number;
  video_count?: number;
}

interface PrivateAlbumMedia {
  id: string;
  album_id: string;
  media_url: string;
  media_type: 'image' | 'video';
  media_name?: string;
  file_size?: number;
  duration?: number;
  created_at: string;
}

export const privateAlbumService = {
  async getUserAlbums(userId: string): Promise<PrivateAlbum[]> {
    const { data, error } = await supabase
      .from('private_albums')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async createAlbum(userId: string, name: string): Promise<PrivateAlbum> {
    const { data, error } = await supabase
      .from('private_albums')
      .insert({
        user_id: userId,
        name,
        album_type: 'private'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async getAlbumMedia(albumId: string): Promise<PrivateAlbumMedia[]> {
    const { data, error } = await supabase
      .from('private_album_media')
      .select('*')
      .eq('album_id', albumId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as PrivateAlbumMedia[];
  },

  async addMediaToAlbum(
    albumId: string, 
    mediaUrl: string, 
    mediaType: 'image' | 'video',
    userId: string,
    mediaName?: string,
    fileSize?: number,
    duration?: number
  ): Promise<PrivateAlbumMedia> {
    const { data, error } = await supabase
      .from('private_album_media')
      .insert({
        album_id: albumId,
        media_url: mediaUrl,
        media_type: mediaType,
        media_name: mediaName,
        file_size: fileSize,
        duration,
        uploaded_by: userId
      })
      .select()
      .single();

    if (error) throw error;
    return data as PrivateAlbumMedia;
  },

  async deleteAlbum(albumId: string): Promise<void> {
    const { error } = await supabase
      .from('private_albums')
      .delete()
      .eq('id', albumId);

    if (error) throw error;
  },

  async deleteMedia(mediaId: string): Promise<void> {
    const { error } = await supabase
      .from('private_album_media')
      .delete()
      .eq('id', mediaId);

    if (error) throw error;
  },

  async getUserAlbumCounts(userId: string): Promise<{ albumCount: number; videoCount: number }> {
    const { data: albums, error } = await supabase
      .from('private_albums')
      .select(`
        id,
        private_album_media!inner(media_type)
      `)
      .eq('user_id', userId);

    if (error) throw error;

    const albumCount = albums?.length || 0;
    const videoCount = albums?.reduce((count, album) => {
      const videoMedia = album.private_album_media?.filter(m => m.media_type === 'video') || [];
      return count + videoMedia.length;
    }, 0) || 0;

    return { albumCount, videoCount };
  }
};